<template>
  <div class="ledger-composer">
    <b-card class="rounded-lg mb-1">
      <div class="d-flex justify-content-between align-items-start flex-wrap">
        <div>
          <h4 class="mb-50">
            {{ headerTitle }}
          </h4>
          <b-badge
            v-if="entry"
            :variant="statusBadgeVariant(statusValue)"
          >
            {{ enumLabel(entry.status, $i18n.locale) }}
          </b-badge>
          <late-posting-badge
            v-if="entry"
            class="ml-50"
            :late="!!entry.is_late_posting"
            :late-from-date="entry.late_from_date"
          />
        </div>
        <div
          v-if="entry && (entry.reverses_entry_id || entry.reversed_by_entry_id)"
          class="small"
        >
          <b-link
            v-if="entry.reverses_entry_id"
            :to="{ name: 'ledger-journals-view', params: { id: entry.reverses_entry_id } }"
          >
            {{ $t('ledger_reverses') }} #{{ entry.reverses_entry_id }}
          </b-link>
          <b-link
            v-if="entry.reversed_by_entry_id"
            :to="{ name: 'ledger-journals-view', params: { id: entry.reversed_by_entry_id } }"
          >
            {{ $t('ledger_reversed_by') }} #{{ entry.reversed_by_entry_id }}
          </b-link>
        </div>
      </div>

      <b-row class="mt-1">
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group :label="$t('ledger_date')">
            <b-form-input
              v-model="form.entry_date"
              type="date"
              :disabled="readOnly"
              :state="dateTouched && !form.entry_date ? false : null"
            />
            <small
              v-if="dateTouched && !form.entry_date"
              class="text-danger"
            >
              {{ $t('ledger_date_required') }}
            </small>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group :label="$t('ledger_book')">
            <v-select
              v-model="form.book"
              :dir="isRtl ? 'rtl' : 'ltr'"
              :options="manualBooks"
              :reduce="val => val.value"
              :label="localeLabel"
              :disabled="readOnly"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="!readOnly"
          cols="12"
          md="3"
        >
          <b-form-group :label="$t('ledger_journal_project')">
            <payroll-project-select
              :value="form.header_project_id"
              :project="form.header_project"
              :placeholder="$t('ledger_project')"
              @input="onHeaderProject"
              @project="onHeaderProjectRecord"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          :md="readOnly ? 6 : 3"
        >
          <b-form-group :label="$t('ledger_description')">
            <b-form-input
              v-model="form.description"
              :disabled="readOnly"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <p
        v-if="closedPeriod"
        class="text-warning mb-0"
      >
        {{ $t('ledger_period_closed') }}
      </p>
    </b-card>

    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner variant="primary" />
    </div>

    <template v-else>
      <div class="d-none d-md-block ">
        <b-table
          :items="lines"
          :fields="lineFields"
          small
          hover
          class="ledger-dense-table"
          thead-class="ledger-sticky-head"
        >
          <template #cell(account)="data">
            <gl-account-select
              v-model="data.item.gl_account_id"
              posting-only
              :accounts="postingAccounts"
              :disabled="readOnly"
              :loading="accountsLoading"
              :placeholder="$t('ledger_account')"
            />
          </template>
          <template #cell(description)="data">
            <b-form-input
              v-model="data.item.description"
              size="sm"
              :disabled="readOnly"
            />
          </template>
          <template #cell(project)="data">
            <span v-if="readOnly">{{ lineProjectLabel(data.item) }}</span>
            <payroll-project-select
              v-else
              :value="data.item.project_id"
              :project="data.item.project"
              :placeholder="$t('ledger_project')"
              @input="id => onLineProject(data.item, id)"
              @project="project => onLineProjectRecord(data.item, project)"
            />
          </template>
          <template #cell(debit)="data">
            <b-form-input
              v-model.number="data.item.debit"
              type="number"
              min="0"
              step="0.01"
              size="sm"
              class="ledger-num"
              :disabled="readOnly"
              @input="onDebitInput(data.item)"
            />
          </template>
          <template #cell(credit)="data">
            <b-form-input
              v-model.number="data.item.credit"
              type="number"
              min="0"
              step="0.01"
              size="sm"
              class="ledger-num"
              :disabled="readOnly"
              @input="onCreditInput(data.item)"
              @keydown.tab="onCreditTab(data.index, $event)"
            />
          </template>
          <template #cell(remove)="data">
            <b-link
              v-if="!readOnly && lines.length > 2"
              class="text-danger"
              @click="removeLine(data.index)"
            >
              <feather-icon
                icon="XIcon"
                size="16"
              />
            </b-link>
          </template>
        </b-table>
      </div>

      <div class="d-md-none">
        <div
          v-for="(line, index) in lines"
          :key="line.key"
          class="ledger-line-card"
        >
          <gl-account-select
            v-model="line.gl_account_id"
            posting-only
            :accounts="postingAccounts"
            :disabled="readOnly"
            :loading="accountsLoading"
            :placeholder="$t('ledger_account')"
          />
          <b-form-input
            v-model="line.description"
            class="mt-50"
            size="sm"
            :disabled="readOnly"
            :placeholder="$t('ledger_description')"
          />
          <div
            v-if="readOnly"
            class="mt-50"
          >
            {{ lineProjectLabel(line) }}
          </div>
          <payroll-project-select
            v-else
            class="mt-50"
            :value="line.project_id"
            :project="line.project"
            :placeholder="$t('ledger_project')"
            @input="id => onLineProject(line, id)"
            @project="project => onLineProjectRecord(line, project)"
          />
          <b-form-radio-group
            v-model="line.side"
            class="mt-50"
            buttons
            button-variant="outline-primary"
            size="sm"
            :disabled="readOnly"
            :options="sideOptions"
            @change="onSideChange(line)"
          />
          <b-form-input
            v-model.number="line.amount"
            type="number"
            min="0"
            step="0.01"
            class="mt-50 ledger-num"
            :disabled="readOnly"
            @input="onAmountInput(line)"
          />
          <b-button
            v-if="!readOnly && lines.length > 2"
            variant="link"
            class="text-danger px-0"
            @click="removeLine(index)"
          >
            {{ $t('ledger_remove_line') }}
          </b-button>
        </div>
      </div>

      <b-button
        v-if="!readOnly"
        variant="outline-primary"
        size="sm"
        class="mt-50 mb-5"
        @click="addLine"
      >
        {{ $t('ledger_add_line') }}
      </b-button>
    </template>

    <div
      class="ledger-imbalance-bar"
      :class="isBalanced ? 'is-balanced' : 'is-warning'"
    >
      <div class="ledger-num">
        {{ $t('ledger_debit') }} {{ money(totalDebit) }}
      </div>
      <div class="ledger-num">
        {{ $t('ledger_credit') }} {{ money(totalCredit) }}
      </div>
      <div class="ledger-num">
        {{ $t('ledger_difference') }} {{ money(difference) }}
      </div>
      <div class="ledger-bar-actions">
        <b-button
          v-if="canSaveDraft"
          size="sm"
          variant="primary"
          :disabled="!canSubmit || saving"
          @click="save(false)"
        >
          {{ $t('ledger_save') }}
        </b-button>
        <b-button
          v-if="canPost"
          size="sm"
          variant="success"
          :disabled="!canSubmit || closedPeriod || saving"
          @click="confirmPost"
        >
          {{ $t('ledger_post') }}
        </b-button>
        <b-button
          v-if="canReverse"
          size="sm"
          variant="warning"
          @click="confirmReverse"
        >
          {{ $t('ledger_reverse') }}
        </b-button>
        <b-button
          v-if="canDeleteDraft"
          size="sm"
          variant="outline-danger"
          @click="confirmDeleteDraft"
        >
          {{ $t('ledger_delete') }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormInput, BButton, BTable, BBadge, BLink,
  BFormRadioGroup, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import {
  getJournalEntry, createJournalEntry, updateJournalEntry, postJournalEntry,
  reverseJournalEntry, deleteJournalEntry,
} from '@/api/accounting/journalEntries'
import { fetchAllChartAccounts } from '@/api/accounting/chartOfAccounts'
import { getAccountingPeriods } from '@/api/accounting/accountingPeriods'
import {
  oneFrom, listFrom, enumValue, enumLabel, JOURNAL_BOOKS, formatLedgerMoney, roundMoney,
  moneyEquals, todayIso, statusBadgeVariant, apiErrorMessage, periodForDate, isPeriodClosed,
} from '@/utils/ledger'
import GlAccountSelect from './components/GlAccountSelect.vue'
import LatePostingBadge from './components/LatePostingBadge.vue'
import PayrollProjectSelect from './components/PayrollProjectSelect.vue'

const newLine = () => ({
  key: `${Date.now()}-${Math.random()}`,
  gl_account_id: null,
  description: '',
  debit: null,
  credit: null,
  amount: null,
  side: 'debit',
  project_id: null,
  project: null,
  project_locked: false,
  customer_id: null,
  employee_id: null,
})

export default {
  name: 'JournalEntryComposer',
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    BTable,
    BBadge,
    BLink,
    BFormRadioGroup,
    BSpinner,
    vSelect,
    FeatherIcon,
    GlAccountSelect,
    LatePostingBadge,
    PayrollProjectSelect,
  },
  data() {
    return {
      JOURNAL_BOOKS,
      loading: false,
      accountsLoading: true,
      saving: false,
      dateTouched: false,
      entry: null,
      postingAccounts: [],
      periods: [],
      lines: [newLine(), newLine()],
      form: {
        entry_date: todayIso(),
        book: 'general',
        description: '',
        header_project_id: null,
        header_project: null,
      },
    }
  },
  computed: {
    manualBooks() {
      const books = JOURNAL_BOOKS.filter(book => book.value !== 'closing')
      if (enumValue(this.entry && this.entry.book) === 'closing') {
        return books.concat([{ value: 'closing', label: 'Closing', label_ar: 'يومية الإقفال' }])
      }
      return books
    },
    isRtl() {
      return this.$store.state.appConfig.layout.isRTL
    },
    localeLabel() {
      return this.$i18n.locale === 'en' ? 'label' : 'label_ar'
    },
    entryId() {
      return this.$route.params.id
    },
    isNew() {
      return this.$route.name === 'ledger-journals-new'
    },
    statusValue() {
      return enumValue(this.entry && this.entry.status)
    },
    readOnly() {
      if (this.isNew) return false
      if (this.statusValue === 'draft') return !this.hasPermission('edit journal_entries')
      return true
    },
    headerTitle() {
      if (this.isNew) return this.$t('ledger_journal_new')
      return this.entry && this.entry.entry_number
        ? `${this.$t('ledger_journal_view')} ${this.entry.entry_number}`
        : this.$t('ledger_journal_view')
    },
    lineFields() {
      return [
        { key: 'account', label: this.$t('ledger_account'), thClass: 'ledger-col-account', tdClass: 'ledger-col-account' },
        { key: 'description', label: this.$t('ledger_description') },
        { key: 'project', label: this.$t('ledger_project') },
        { key: 'debit', label: this.$t('ledger_debit') },
        { key: 'credit', label: this.$t('ledger_credit') },
        { key: 'remove', label: '' },
      ]
    },
    sideOptions() {
      return [
        { text: this.$t('ledger_debit'), value: 'debit' },
        { text: this.$t('ledger_credit'), value: 'credit' },
      ]
    },
    totalDebit() {
      return roundMoney(this.lines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0))
    },
    totalCredit() {
      return roundMoney(this.lines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0))
    },
    difference() {
      return roundMoney(this.totalDebit - this.totalCredit)
    },
    isBalanced() {
      return moneyEquals(this.totalDebit, this.totalCredit) && this.totalDebit > 0
    },
    validLineCount() {
      return this.lines.filter(line => line.gl_account_id && ((Number(line.debit) || 0) > 0 || (Number(line.credit) || 0) > 0)).length
    },
    canSubmit() {
      return this.isBalanced && this.validLineCount >= 2
    },
    closedPeriod() {
      const period = periodForDate(this.periods, this.form.entry_date)
      return period ? isPeriodClosed(period) : false
    },
    canSaveDraft() {
      if (this.readOnly) return false
      if (this.isNew) return this.hasPermission('create journal_entries')
      return this.statusValue === 'draft' && this.hasPermission('edit journal_entries')
    },
    canPost() {
      if (!this.hasPermission('post journal_entries')) return false
      if (this.isNew) return this.hasPermission('create journal_entries')
      return this.statusValue === 'draft'
    },
    canReverse() {
      return this.statusValue === 'posted' && this.hasPermission('reverse journal_entries')
    },
    canDeleteDraft() {
      return this.statusValue === 'draft' && this.hasPermission('edit journal_entries')
    },
  },
  watch: {
    $route() {
      this.boot()
    },
  },
  mounted() {
    this.boot()
  },
  methods: {
    enumValue,
    enumLabel,
    statusBadgeVariant,
    money(value) {
      return formatLedgerMoney(value, this.$i18n.locale)
    },
    lineProjectLabel(line) {
      const project = line && line.project
      if (project) {
        const name = project.name || ''
        const code = project.project_number || project.code || ''
        if (code && name) return `${code} — ${name}`
        if (name || code) return name || code
      }
      return line && line.project_id ? `#${line.project_id}` : ''
    },
    blankHeader() {
      return {
        entry_date: todayIso(),
        book: 'general',
        description: '',
        header_project_id: null,
        header_project: null,
      }
    },
    async boot() {
      await Promise.all([this.loadPostingAccounts(), this.loadPeriods()])
      if (this.isNew) {
        this.entry = null
        this.dateTouched = false
        this.form = this.blankHeader()
        this.lines = [newLine(), newLine()]
        return
      }
      await this.loadEntry()
    },
    async loadPostingAccounts() {
      this.accountsLoading = true
      try {
        const rows = await fetchAllChartAccounts({
          per_page: 100,
          is_active: true,
          accepts_posting: true,
        })
        this.postingAccounts = rows.filter(row => row.accepts_posting && row.is_active)
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.accountsLoading = false
      }
    },
    async loadPeriods() {
      try {
        const res = await getAccountingPeriods()
        this.periods = listFrom(res)
      } catch (e) {
        this.periods = []
      }
    },
    async loadEntry() {
      this.loading = true
      try {
        const res = await getJournalEntry(this.entryId)
        const entry = oneFrom(res)
        this.entry = entry
        this.dateTouched = false
        this.form = {
          entry_date: entry.entry_date || '',
          book: enumValue(entry.book) || 'general',
          description: entry.description || '',
          header_project_id: null,
          header_project: null,
        }
        const lines = Array.isArray(entry.lines) ? entry.lines : []
        this.lines = (lines.length ? lines : [newLine(), newLine()]).map(line => {
          const debit = Number(line.debit) || 0
          const credit = Number(line.credit) || 0
          return {
            key: line.id || `${Date.now()}-${Math.random()}`,
            gl_account_id: line.gl_account_id,
            description: line.description || '',
            debit: debit || null,
            credit: credit || null,
            amount: debit || credit || null,
            side: credit > 0 ? 'credit' : 'debit',
            project_id: line.project_id || null,
            project: line.project || null,
            project_locked: line.project_id != null,
            customer_id: line.customer_id,
            employee_id: line.employee_id,
          }
        })
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
    onDebitInput(line) {
      if (Number(line.debit) > 0) {
        line.credit = null
        line.side = 'debit'
        line.amount = line.debit
      }
    },
    onCreditInput(line) {
      if (Number(line.credit) > 0) {
        line.debit = null
        line.side = 'credit'
        line.amount = line.credit
      }
    },
    onSideChange(line) {
      this.onAmountInput(line)
    },
    onAmountInput(line) {
      const amount = Number(line.amount) || 0
      if (line.side === 'credit') {
        line.credit = amount || null
        line.debit = null
      } else {
        line.debit = amount || null
        line.credit = null
      }
    },
    onCreditTab(index, event) {
      if (event.shiftKey) return
      if (index === this.lines.length - 1 && !this.readOnly) {
        event.preventDefault()
        this.addLine()
        this.$nextTick(() => {
          const selects = this.$el.querySelectorAll('.ledger-account-select input')
          const next = selects[index + 1]
          if (next) next.focus()
        })
      }
    },
    addLine() {
      const line = newLine()
      if (this.form.header_project_id) {
        line.project_id = this.form.header_project_id
        line.project = this.form.header_project
      }
      this.lines.push(line)
    },
    onHeaderProject(id) {
      this.form.header_project_id = id || null
      if (id) return
      this.form.header_project = null
      this.lines.forEach(line => {
        if (line.project_locked) return
        line.project_id = null
        line.project = null
      })
    },
    onHeaderProjectRecord(project) {
      this.form.header_project = project || null
      if (!project) return
      this.lines.forEach(line => {
        if (line.project_locked) return
        line.project_id = project.id
        line.project = project
      })
    },
    onLineProject(line, id) {
      const next = id || null
      if ((line.project_id || null) === next) return
      line.project_id = next
      line.project_locked = true
      if (!next) line.project = null
    },
    onLineProjectRecord(line, project) {
      if (!project && !line.project_locked && this.form.header_project_id) return
      line.project = project || null
      if (project) line.project_id = project.id
    },
    removeLine(index) {
      if (this.lines.length <= 2) return
      this.lines.splice(index, 1)
    },
    buildPayload(post) {
      return {
        entry_date: this.form.entry_date,
        book: this.form.book,
        description: this.form.description,
        accounting_period_id: null,
        post: !!post,
        lines: this.lines
          .filter(line => line.gl_account_id)
          .map(line => ({
            gl_account_id: line.gl_account_id,
            debit: roundMoney(line.debit) || 0,
            credit: roundMoney(line.credit) || 0,
            description: line.description || null,
            project_id: line.project_id ? Number(line.project_id) : null,
            customer_id: line.customer_id || null,
            employee_id: line.employee_id || null,
          })),
      }
    },
    guardDate() {
      if (this.form.entry_date) return true
      this.dateTouched = true
      this.toast(this.$t('ledger_date_required'), 'warning')
      return false
    },
    guardUnbalanced() {
      if (!this.canSubmit) {
        this.toast(
          this.validLineCount < 2 ? this.$t('ledger_need_two_lines') : this.$t('ledger_cannot_save_unbalanced'),
          'warning',
        )
        return false
      }
      return true
    },
    async save(post) {
      if (!this.guardDate() || !this.guardUnbalanced()) return
      this.saving = true
      try {
        let res
        if (this.isNew) {
          res = await createJournalEntry(this.buildPayload(post))
        } else if (post && this.entryId) {
          await updateJournalEntry(this.entryId, this.buildPayload(false))
          res = await postJournalEntry(this.entryId)
        } else {
          res = await updateJournalEntry(this.entryId, this.buildPayload(false))
        }
        const saved = oneFrom(res)
        this.toast(post ? this.$t('ledger_posted_success') : this.$t('ledger_created_success'), 'success')
        if (saved && saved.id && (this.isNew || post)) {
          this.$router.replace({ name: 'ledger-journals-view', params: { id: saved.id } })
        } else {
          await this.loadEntry()
        }
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.saving = false
      }
    },
    async confirmPost() {
      if (!this.guardDate()) return
      if (this.closedPeriod) {
        this.toast(this.$t('ledger_period_closed'), 'warning')
        return
      }
      if (!this.guardUnbalanced()) return
      const result = await Swal.fire({
        text: this.$t('ledger_confirm_post'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_post'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: {
          confirmButton: 'btn btn-success',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      if (this.isNew) {
        await this.save(true)
        return
      }
      this.saving = true
      try {
        if (this.statusValue === 'draft') {
          await updateJournalEntry(this.entryId, this.buildPayload(false))
        }
        const res = await postJournalEntry(this.entryId)
        this.toast(this.$t('ledger_posted_success'), 'success')
        this.entry = oneFrom(res)
        await this.loadEntry()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.saving = false
      }
    },
    async confirmReverse() {
      const result = await Swal.fire({
        text: this.$t('ledger_confirm_reverse'),
        input: 'text',
        inputPlaceholder: this.$t('ledger_reverse_description'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_reverse'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: {
          confirmButton: 'btn btn-warning',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      try {
        const body = result.value ? { description: result.value } : {}
        const res = await reverseJournalEntry(this.entryId, body)
        const created = oneFrom(res)
        this.toast(this.$t('ledger_reversed_success'), 'success')
        if (created && created.id) {
          this.$router.push({ name: 'ledger-journals-view', params: { id: created.id } })
        }
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      }
    },
    async confirmDeleteDraft() {
      const result = await Swal.fire({
        text: this.$t('ledger_confirm_delete_draft'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_delete'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      try {
        await deleteJournalEntry(this.entryId)
        this.toast(this.$t('ledger_deleted_success'), 'success')
        this.$router.push({ name: 'ledger-journals' })
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      }
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.ledger-composer {
  padding-bottom: 5.5rem;
}
.ledger-lines-scroll {
  overflow-x: auto;
}
.ledger-composer .ledger-dense-table {
  width: 100%;
  table-layout: auto;
}
.ledger-composer .ledger-col-account {
  min-width: 200px;
  width: 25%;
}
.ledger-num {
  font-variant-numeric: tabular-nums;
  text-align: end;
}
.ledger-line-card {
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
}
.ledger-imbalance-bar {
  position: sticky;
  bottom: 0;
  z-index: 15;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem 1rem;
  padding: 0.75rem 1rem;
  background: #fff;
  border-top: 2px solid #d8d6de;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.06);
}
.ledger-imbalance-bar.is-balanced {
  border-top-color: #28c76f;
}
.ledger-imbalance-bar.is-warning {
  border-top-color: #ff9f43;
  background: #fff8ef;
}
.ledger-bar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}
.ledger-sticky-head th {
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
}
@media (max-width: 768px) {
  .ledger-composer {
    padding-bottom: 7rem;
  }
  .ledger-imbalance-bar {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
  }
}
</style>
